import React, { useEffect, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import CandidateInterestTable, {
  COLUMNS_CONSTANTS as INTEREST_COLUMNS,
} from "./candidate_interest/CandidateInterestTable";
import sh from "bundles/common/utils/sh";
import exportCsv from "bundles/common/utils/exportCsv";
import { EMPTY_CANDIDATE_INTEREST_FILTERS } from "bundles/Internal/components/candidate_interest/CandidateInterestTableFilters";

function buildSearchFilters(activeFilters) {
  const filters = {};
  let hasAny = false;

  if (activeFilters.availability?.length) {
    filters.candidate_availability = activeFilters.availability;
    hasAny = true;
  }

  if (activeFilters.countries?.length) {
    filters.candidate_country = activeFilters.countries;
    hasAny = true;
  }

  if (activeFilters.region?.length) {
    filters.candidate_region = activeFilters.region;
    hasAny = true;
  }

  return hasAny ? filters : null;
}

const CandidateInterestsIndexPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const [interests, setInterests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_CANDIDATE_INTEREST_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const fetchInterests = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const hasQuery = Boolean(String(query || "").trim());
    const searchFilters = buildSearchFilters(activeFilters);
    const hasFilters = searchFilters !== null;

    setLoading(true);
    sh.get("/internal/search/candidate_interests", {
      params: {
        query: hasQuery ? query : undefined,
        page: pageNum,
        limit,
        ...(hasFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setInterests(payload);
        if (res.data?.pagy) {
          setTotalCount(res.data.pagy.count || payload.length);
        } else {
          setTotalCount(payload.length);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch candidate interests", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchInterests(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchInterests, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchInterests(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchInterests, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleExportCsv = useCallback((ids) => {
    exportCsv("candidate_interests", ids, authenticity_token, INTEREST_COLUMNS)
      .then(() => enqueueSnackbar("CSV exported", { variant: "success" }))
      .catch(() => enqueueSnackbar("Failed to export CSV", { variant: "error" }));
  }, [authenticity_token, enqueueSnackbar]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <CandidateInterestTable
        interests={interests}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        onInterestSelect={(interest) => {
          if (!interest?.candidate_id) return;
          navigate(`/internal/candidates/${interest.candidate_id}`, {
            state: { from: location.pathname },
          });
        }}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        selectable
        selectedRows={selectedRows}
        onRowSelect={setSelectedRows}
        onExportCsv={handleExportCsv}
      />
    </Box>
  );
};

export default CandidateInterestsIndexPage;
